/**
 * Acesso: a sessao, a troca de empresa e as rotas antes da sessao (tela de
 * acesso publica, recuperacao de senha e situacao de manutencao).
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import { api } from '../client';

export interface Branding {
  display_name: string;
  logo_url: string;
  logo_symbol_url: string;
  favicon_url: string;
  primary_color: string;
  accent_color: string;
  theme_color: string;
  background_color: string;
  pwa_name: string;
  pwa_short_name: string;
  locale: string;
  timezone: string;
}

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  plan: string;
  status: string;
  branding: Branding;
  /** Acesso da empresa que vale com a sessao aberta (P-ACE-1). */
  access?: TenantAccess;
}

/**
 * O que a moldura precisa do acesso da empresa: o aviso de manutencao, o bloqueio
 * por inatividade e o contato. Vem na sessao porque toda pessoa da empresa precisa,
 * e a leitura das configuracoes exige permissao que nem todas tem.
 */
export interface TenantAccess {
  /** Mensagem do aviso de manutencao da empresa. Vazia com o aviso desligado. */
  maintenance_warning: string;
  /** Minutos de inatividade que bloqueiam a sessao. Nulo e desligado. */
  session_lock_idle_minutes: number | null;
  /** Prazo de validade da senha em dias (R-1). Nulo e desligado. */
  password_expiry_days?: number | null;
  support_url: string;
}

export interface UserView {
  id: string;
  name: string;
  email: string;
  last_login_at?: string;
}

export interface SessionView {
  user: UserView;
  tenant: TenantSummary;
  permissions: string[];
  features: string[];
  /** A pessoa enxerga so a propria corretora (portal da corretora). */
  broker_scoped?: boolean;
  /**
   * Informa se a identidade administra a plataforma.
   *
   * Orienta a interface, como as permissoes: mostra o caminho para /admin. Quem decide
   * e o servidor, a cada requisicao, e a resposta nao vem do papel na empresa.
   */
  platform_admin: boolean;

  // --- P-USU-1: o que o OnPoint devolve em Security/GetUserInfo ------------

  /** Corretora de quem tem escopo de corretora. Nulo para a seguradora. */
  broker?: SessionBroker | null;
  /** Perfis da pessoa. O SeguraCore aceita mais de um. */
  roles?: SessionRole[];
  /** Publico da sessao: seguradora, corretora ou assessoria. */
  audience?: 'seguradora' | 'corretora' | 'assessoria';
  /** Tela que abre no acesso. "/" e o Painel. */
  home_route?: string;
  /** Documento obrigatorio da corretora pendente. Falso ate o pacote da corretora. */
  has_pending_documents?: boolean;
  /**
   * Assessoria de quem entrou pela assessoria (P-ACE-2), com a corretora pela qual a
   * sessao atua. Nula para quem nao e da assessoria.
   */
  advisor?: SessionAdvisor | null;
}

/** A assessoria da sessao (P-ACE-2). */
export interface SessionAdvisor {
  id: string;
  name: string;
  /** IsAssessoriaMaster do OnPoint (USU-161). So a marca: o efeito nao tem fonte. */
  master: boolean;
  /** Corretora pela qual a sessao atua. Nula antes da escolha. */
  acting_broker: SessionBroker | null;
}

/** Corretora da sessao (P-USU-1). */
export interface SessionBroker {
  id: string;
  legal_name: string;
}

/** Perfil da pessoa, como a sessao o mostra (P-USU-1). */
export interface SessionRole {
  id: string;
  key: string;
  name: string;
  home_route: string;
}

export interface TokenPair {
  access_token: string;
  access_expires_at: string;
  refresh_token: string;
  refresh_expires_at: string;
  token_type: string;
}

export interface LoginResponse {
  tokens: TokenPair;
  session: SessionView;
}

export interface Workspace {
  member_id: string;
  tenant: TenantSummary;
  current: boolean;
}

export const workspaces = {
  list: (signal?: AbortSignal) =>
    api.get<Workspace[]>('/api/v1/auth/workspaces', signal),
  forCredentials: (email: string, password: string) =>
    api.post<Workspace[]>('/api/v1/auth/workspaces', { email, password }),
};

// --- Acesso publico, recuperacao de senha e manutencao (P-ACE-1) -----------

/**
 * Configuracao da tela de acesso antes da sessao, no formato do portal do OnPoint.
 *
 * Com `resolved` falso a tela e a da plataforma: a mesma resposta vale para empresa
 * inexistente, suspensa e para a que nao publicou a marca.
 */
export interface PortalConfig {
  resolved: boolean;
  identified_by: 'rota' | 'dominio' | null;
  identification: 'rota_e_dominio' | 'rota' | 'dominio' | 'desligada';
  tenant_slug: string | null;
  display_name: string;
  layout: {
    common: {
      logo: string;
      logo_symbol: string;
      logo_white: string;
      favicon: string;
      gradient_background: boolean;
      gradient_background_colors: string[];
      primary_color: string;
      accent_color: string;
      theme_color: string;
      background_color: string;
    };
    login: {
      title: string;
      subtitle1: string;
      subtitle2: string;
      background_image: string;
    };
  };
  enable_corretor_access: boolean;
  enable_advisor_insurance: boolean;
  enable_registration_broker: boolean;
  enable_password_login: boolean;
  redirect_login_url: string | null;
  warn_maintenance: boolean;
  maintenance_message: string;
  support_url: string;
}

export interface MaintenancePublicWindow {
  starts_at: string;
  /** Nulo e sem previsao de retorno. */
  ends_at: string | null;
  message: string;
}

export interface MaintenanceStatus {
  under_maintenance: boolean;
  current: MaintenancePublicWindow | null;
  next: MaintenancePublicWindow | null;
}

export interface PasswordRecoveryReceipt {
  accepted: boolean;
  /** Se a plataforma envia e-mail. E da plataforma, e nao da conta. */
  email_delivery_available: boolean;
  validity_minutes: number;
}

export interface PasswordResetPreview {
  email: string;
  expires_at: string;
}

/**
 * Rotas do acesso.
 *
 * As publicas nao mandam token: quem chama ainda nao tem sessao, e a recusa delas
 * nao pode virar tentativa de renovar uma sessao que nao existe.
 */
/** Conta ativada pelo link do e-mail (ADR 0096). */
export interface AccountActivationResult {
  activated: boolean;
  email: string;
  name: string;
  tenant_slug: string;
  tenant_name: string;
  /** Tela de acesso da pessoa, como `/entrar/{slug}/corretor`. */
  login_path: string;
}

/** Resposta do pedido de link novo: a mesma exista ou nao a conta pendente. */
export interface AccountActivationResendReceipt {
  accepted: boolean;
  email_delivery_available: boolean;
  validity_hours: number;
}

export const acesso = {
  portalConfig: (
    consulta: { tenantSlug?: string; domain?: string },
    signal?: AbortSignal,
  ) => {
    const parametros = new URLSearchParams();
    if (consulta.tenantSlug) parametros.set('tenant_slug', consulta.tenantSlug);
    if (consulta.domain) parametros.set('domain', consulta.domain);
    const sufixo = parametros.toString();
    return api.get<PortalConfig>(
      `/api/v1/public/portal-config${sufixo ? `?${sufixo}` : ''}`,
      signal,
    );
  },
  maintenanceStatus: (signal?: AbortSignal) =>
    api.get<MaintenanceStatus>('/api/v1/public/maintenance-status', signal),
  requestRecovery: (email: string) =>
    api.post<PasswordRecoveryReceipt>(
      '/api/v1/auth/password-recovery',
      { email },
      { skipAuth: true },
    ),
  resetPreview: (token: string, signal?: AbortSignal) =>
    api.get<PasswordResetPreview>(
      `/api/v1/auth/password-resets/${encodeURIComponent(token)}`,
      signal,
    ),
  resetPassword: (token: string, password: string) =>
    api.post<void>(
      `/api/v1/auth/password-resets/${encodeURIComponent(token)}`,
      { password },
      { skipAuth: true },
    ),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post<void>('/api/v1/auth/password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),
  /** Ativa a conta pelo link do e-mail. O segredo vem do fragmento do endereco. */
  activateAccount: (activationId: string, token: string) =>
    api.post<AccountActivationResult>(
      `/api/v1/auth/account-activations/${encodeURIComponent(activationId)}`,
      { token },
      { skipAuth: true },
    ),
  /** Pede um link novo de ativacao para a conta pendente do e-mail. */
  resendActivation: (email: string) =>
    api.post<AccountActivationResendReceipt>(
      '/api/v1/auth/account-activations/resend',
      { email },
      { skipAuth: true },
    ),
};

// --- Acesso da assessoria (P-ACE-2) -------------------------------------------

/** Corretora pela qual o assessor pode atuar. */
export interface AdvisorSessionBroker {
  id: string;
  number: number;
  legal_name: string;
  document: string;
  stage: string;
}

export interface AdvisorSessionBrokers {
  items: AdvisorSessionBroker[];
  /** Corretora pela qual a sessao atua. Nula antes da escolha. */
  acting_broker_id: string | null;
}

/**
 * Corretoras da assessoria e a troca da corretora da sessao. Quem decide e o servidor:
 * so a corretora da assessoria e aceita, e o vinculo e conferido a cada requisicao.
 */
export const advisorSession = {
  brokers: (signal?: AbortSignal) =>
    api.get<AdvisorSessionBrokers>('/api/v1/auth/advisor-brokers', signal),
  switchBroker: (brokerId: string) =>
    api.post<SessionView>('/api/v1/auth/switch-broker', { broker_id: brokerId }),
};
